import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Trash2, ShieldCheck, Volume2, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralsPage = () => {
  const { t, speak } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    referring_doctor: '',
    referred_to_doctor: '',
    speciality: '',
    reason: '',
    referral_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/health/referrals`);
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/health/referrals`, formData);
      toast.success(t('recordAdded'));
      setDialogOpen(false);
      setFormData({ referring_doctor: '', referred_to_doctor: '', speciality: '', reason: '', referral_date: '', notes: '' });
      fetchEntries();
    } catch (error) {
      toast.error('Failed to add referral');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this referral?')) return;
    try {
      await axios.delete(`${API_URL}/health/referrals/${id}`);
      toast.success(t('recordDeleted'));
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-nav">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-orange-100">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('referrals')}</h1>
              <p className="text-slate-500">{entries.length} referrals</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0D2340]" data-testid="add-referral-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('addReferral')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">{t('addReferral')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('referringDoctor')}</Label>
                  <Input
                    value={formData.referring_doctor}
                    onChange={(e) => setFormData({ ...formData, referring_doctor: e.target.value })}
                    placeholder="Dr. Ram Kumar"
                    required
                    data-testid="referring-doctor-input"
                  />
                </div>
                <div>
                  <Label>{t('referredTo')}</Label>
                  <Input
                    value={formData.referred_to_doctor}
                    onChange={(e) => setFormData({ ...formData, referred_to_doctor: e.target.value })}
                    placeholder="Dr. Sita Sharma"
                    required
                    data-testid="referred-to-input"
                  />
                </div>
                <div>
                  <Label>{t('speciality')}</Label>
                  <Input
                    value={formData.speciality}
                    onChange={(e) => setFormData({ ...formData, speciality: e.target.value })}
                    placeholder="e.g., Cardiology, Orthopedics"
                    required
                    data-testid="speciality-input"
                  />
                </div>
                <div>
                  <Label>{t('reason')}</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Reason for referral..."
                    required
                    data-testid="reason-input"
                  />
                </div>
                <div>
                  <Label>{t('referralDate')}</Label>
                  <Input
                    type="date"
                    value={formData.referral_date}
                    onChange={(e) => setFormData({ ...formData, referral_date: e.target.value })}
                    required
                    data-testid="referral-date-input"
                  />
                </div>
                <div>
                  <Label>{t('notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    data-testid="referral-notes-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#0D2340]" data-testid="save-referral-btn">
                    {t('save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-10">{t('loading')}</div>
        ) : entries.length === 0 ? (
          <Card className="text-center py-10">
            <CardContent>
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t('noData')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className="card-interactive" data-testid={`referral-card-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-semibold text-[#0D2340]">{entry.referring_doctor}</span>
                        <ArrowRight className="h-4 w-4 text-slate-400" />
                        <span className="font-semibold text-[#0D2340]">{entry.referred_to_doctor}</span>
                        <Badge className="bg-[#FFB300] text-slate-900">{entry.speciality}</Badge>
                      </div>
                      <p className="text-slate-600 mb-2">{entry.reason}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span>{t('referralDate')}: {entry.referral_date}</span>
                      </div>
                      {entry.notes && <p className="text-sm text-slate-500 mt-2">{entry.notes}</p>}
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="blockchain-badge text-xs">
                          <ShieldCheck className="h-3 w-3" />
                          {entry.blockchain_hash?.slice(0, 12)}...
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => speak(`Referral from ${entry.referring_doctor} to ${entry.referred_to_doctor} for ${entry.speciality}. ${entry.reason}`)}
                        data-testid={`speak-referral-${entry.id}`}
                      >
                        <Volume2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entry.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-referral-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReferralsPage;
